/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import { FormattedMessage } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/utility/cross';
import { Anchor, Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';

import { adminManageUserInviteLinkHref } from '../../utils';
import { messages } from '../i18n';

const styles = cssMap({
	headingWrapper: {
		font: token(
			'font.body.large',
			'normal 400 16px/24px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, system-ui, "Helvetica Neue", sans-serif',
		),
	},
	heading: {
		color: token('color.text.subtle', '#44546F'),
		fontWeight: token('font.weight.medium', '500'),
	},
	toggle: {
		marginInlineStart: token('space.negative.075', '-6px'),
	},
	anchor: {
		textDecoration: 'none',
	},
	wrapper: {
		marginBlockStart: token('space.200', '16px'),
		backgroundColor: token('color.background.discovery', '#F3F0FF'),
		borderRadius: token('border.radius', '3px'),
		paddingBlock: token('space.200', '16px'),
		paddingInline: token('space.200', '16px'),
	},
});

export const TurnOnInviteLink: React.FC<{
	handleAllowLink: () => Promise<void>;
	handleCloseTurnOnInviteLink: () => void;
	orgId?: string;
}> = ({ handleAllowLink, orgId, handleCloseTurnOnInviteLink }) => {
	return (
		<Stack xcss={styles.wrapper} space="space.100">
			<Inline grow="fill" alignBlock="stretch" spread="space-between">
				<Box as="span" xcss={styles.headingWrapper}>
					<Box as="span" xcss={styles.heading}>
						<FormattedMessage {...messages.turnOnShareableLinkTitle} />
					</Box>
				</Box>
				<IconButton
					appearance="subtle"
					label="Close modal"
					icon={CrossIcon}
					spacing="compact"
					onClick={handleCloseTurnOnInviteLink}
					testId="testId-shareablelink-turn-on-invite-dismiss"
				/>
			</Inline>
			<Text color="color.text.subtle">
				<FormattedMessage
					{...messages.turnOnShareableLinkHelpText}
					values={{
						a: (chunks) => (
							<Anchor
								xcss={styles.anchor}
								href={orgId ? adminManageUserInviteLinkHref(orgId) : ''}
								target="_blank"
							>
								{chunks}
							</Anchor>
						),
					}}
				/>
			</Text>
			<Inline alignBlock="center" xcss={styles.toggle} space="space.100">
				<Toggle
					id="toggle-allow-link"
					label="allowShareableLink"
					onChange={handleAllowLink}
					testId="testId-shareablelink-turn-on-invite-toggle"
				/>
				<Text size="medium">
					<FormattedMessage {...messages.turnOnShareableLinkToggleLabel} />
				</Text>
			</Inline>
		</Stack>
	);
};
